import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import Navbar from '../components/Navbar';
import StatsBar from '../components/StatsBar';
import JobTable from '../components/JobTable';
import PinBoard from '../components/PinBoard';
import JobModal from '../components/JobModal';
import AddJobModal from '../components/AddJobModal';
import CompareModal from '../components/CompareModal';
import UpgradeModal from '../components/UpgradeModal';
import ProCelebration from '../components/ProCelebration';
import CalendarPage from './CalendarPage';
import AnalyzePage from './AnalyzePage';
import PreparePage from './PreparePage';
import './Dashboard.css';

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const { settings } = useSettings();
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', stage: 'all', search: '' });
  const [sort, setSort] = useState({ by: settings.defaultSort, order: 'desc' });
  const [viewMode, setViewMode] = useState(settings.defaultView);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationPlan, setCelebrationPlan] = useState(null);
  const [activePage, setActivePage] = useState('tracker');
  const [pendingAddEvent, setPendingAddEvent] = useState(false);
  const [preselectedPrepEvent, setPreselectedPrepEvent] = useState(null);
  // Track which pages have been visited so they stay mounted (no refetch on tab switch)
  const [mountedPages, setMountedPages] = useState(() => new Set(['tracker']));

  useEffect(() => {
    setMountedPages(prev => new Set([...prev, activePage]));
  }, [activePage]);

  const fetchJobs = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.stage !== 'all' && { stage: filters.stage }),
        ...(filters.search && { search: filters.search }),
        sortBy: sort.by,
        sortOrder: sort.order
      });
      const res = await api.get(`/api/jobs?${params}`);
      setJobs(res.data.jobs);
    } catch { toast.error('Failed to load jobs'); }
    finally { setLoading(false); }
  }, [filters, sort]);

  const fetchAllJobs = useCallback(async () => {
    try {
      const res = await api.get('/api/jobs');
      setAllJobs(res.data.jobs);
    } catch {}
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/api/jobs/stats/summary');
      setStats(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);
  useEffect(() => { fetchAllJobs(); }, [fetchAllJobs]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Handle redirect back from PayMongo checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment === 'success') {
      window.history.replaceState({}, '', '/app');
      api.post('/api/payment/confirm').then(res => {
        if (res.data.upgraded) {
          refreshUser();
          setCelebrationPlan(res.data.plan);
          setShowCelebration(true);
        } else {
          toast.success('Payment received! Your account may take a moment to update.');
          refreshUser();
        }
      }).catch(() => {
        toast.success('Payment received! Refreshing your account...');
        refreshUser();
      });
    } else if (payment === 'cancelled') {
      toast('Payment was cancelled.', { icon: '↩' });
      window.history.replaceState({}, '', '/app');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddJob = async (jobData) => {
    try {
      const res = await api.post('/api/jobs', jobData);
      toast.success('Job added!');
      setShowAddModal(false);
      setActivePage('tracker');
      fetchJobs(); fetchAllJobs(); fetchStats();
      return res.data.job;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add job');
      throw err;
    }
  };

  const handleUpdateJob = async (id, data) => {
    try {
      const res = await api.put(`/api/jobs/${id}`, data);
      toast.success('Updated!');
      setJobs(prev => prev.map(j => j._id === id ? res.data.job : j));
      setAllJobs(prev => prev.map(j => j._id === id ? res.data.job : j));
      if (selectedJob?._id === id) setSelectedJob(res.data.job);
      fetchStats();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to update'); }
  };

  const handleDeleteJob = async (id) => {
    try {
      await api.delete(`/api/jobs/${id}`);
      toast.success('Job removed');
      setJobs(prev => prev.filter(j => j._id !== id));
      setAllJobs(prev => prev.filter(j => j._id !== id));
      setSelectedJob(null);
      fetchStats();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="dashboard">
      <Navbar
        onAddJob={() => setShowAddModal(true)}
        onAddEvent={() => { setActivePage('calendar'); setPendingAddEvent(true); }}
        activePage={activePage}
        setActivePage={setActivePage}
      />

      {/* tracker is always mounted */}
      <div className={`page-view ${activePage === 'tracker' ? 'page-active' : ''}`}>
        <main className="dashboard-main">
          <div className="dashboard-header">
            <div>
              <h1 className="dashboard-title">
                Good to see you, <span className="name-accent">{user?.name?.split(' ')[0]}</span>
              </h1>
              <p className="dashboard-sub">Here's where your job search stands today.</p>
            </div>
            <div className="dashboard-header-actions">
              <div className="view-toggle">
                <button className={`view-toggle-btn ${viewMode === 'board' ? 'active' : ''}`}
                  onClick={() => setViewMode('board')} title="Pin board view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                    <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                    <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
                  </svg>
                  Board
                </button>
                <button className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                  onClick={() => setViewMode('table')} title="Detailed table view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
                    <line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/>
                    <line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                  </svg>
                  Detailed
                </button>
              </div>
              {allJobs.length >= 2 && (
                <button className="btn-compare" onClick={() => setActivePage('analyze')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  Analyze
                </button>
              )}
              <button className="btn-add-primary" onClick={() => setShowAddModal(true)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Application
              </button>
            </div>
          </div>

          <StatsBar stats={stats} />

          {viewMode === 'table' ? (
            <JobTable
              jobs={jobs}
              loading={loading}
              filters={filters}
              setFilters={setFilters}
              sort={sort}
              setSort={setSort}
              onRowClick={setSelectedJob}
              onAddJob={() => setShowAddModal(true)}
            />
          ) : loading ? (
            <div className="board-skeleton">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="board-col-skeleton">
                  <div className="board-col-skeleton-hdr" />
                  {[...Array(2 + (i % 2))].map((_, j) => (
                    <div key={j} className="board-card-skeleton" />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <PinBoard
              jobs={jobs}
              onCardClick={setSelectedJob}
              onStatusChange={(id, status) => handleUpdateJob(id, { status })}
            />
          )}
        </main>
      </div>

      {/* other pages: mounted on first visit, hidden when inactive */}
      {mountedPages.has('calendar') && (
        <div className={`page-view ${activePage === 'calendar' ? 'page-active' : ''}`}>
          <CalendarPage jobs={allJobs} onJobUpdate={(id, data) => handleUpdateJob(id, data)} onPrepare={(ev) => { setPreselectedPrepEvent(ev); setActivePage('prepare'); }} onUpgrade={() => setShowUpgrade(true)} openNewEvent={pendingAddEvent} onEventOpened={() => setPendingAddEvent(false)} />
        </div>
      )}
      {mountedPages.has('analyze') && (
        <div className={`page-view ${activePage === 'analyze' ? 'page-active' : ''}`}>
          <AnalyzePage jobs={allJobs} onUpgrade={() => setShowUpgrade(true)} />
        </div>
      )}
      {mountedPages.has('prepare') && (
        <div className={`page-view ${activePage === 'prepare' ? 'page-active' : ''}`}>
          <PreparePage jobs={allJobs} preselectedEvent={preselectedPrepEvent} onUpgrade={() => setShowUpgrade(true)} />
        </div>
      )}

      {selectedJob && (
        <JobModal
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          onUpdate={handleUpdateJob}
          onDelete={handleDeleteJob}
        />
      )}

      {showAddModal && (
        <AddJobModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddJob}
          onUpgrade={() => { setShowAddModal(false); setShowUpgrade(true); }}
        />
      )}
      {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
      {showCelebration && <ProCelebration plan={celebrationPlan} onClose={() => setShowCelebration(false)} />}

      {showCompare && (
        <CompareModal
          jobs={allJobs}
          onClose={() => setShowCompare(false)}
          onUpgrade={() => { setShowCompare(false); setShowUpgrade(true); }}
        />
      )}
    </div>
  );
};

export default Dashboard;
